/**
 * Paged store for the legacy Manage Returns list page (no tabs / tab_counts).
 * TODO(cleanup): Remove with rma-list-page/ when the redesign ships. Checklist: ai-docs/manage-returns-redesign.md Section 6.1.
 */
// eslint-disable-next-line
import { makeObservable, observable, runInAction } from "mobx";
import PagedDataStore, { IFetchListArgs } from "../../../../stores/abstract-paged-data-store";
import RmaListItemStore from "./rma-item-store";
import RmaListService, { IFetchRmaListArgs, MANAGE_RETURNS_BACKEND } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import RmaListFiltersStore from "./rma-list-filters-store";
import rmaPageMainStore from "../../rma-page/stores/rma-page-main-store";
import { IIframeService } from "../../../../services/iframe-service/iframe-service";

export { RMA_QUICK_FILTERS } from "./rma-quick-filters";

class RmaPagedListStore extends PagedDataStore<RmaListItemStore> {
    @observable private _rmaListFiltersStore: RmaListFiltersStore = new RmaListFiltersStore();

    constructor() {
        super();
        makeObservable(this);
    }
    readonly perPage: number = 20;
    protected get _rmaListService(): RmaListService { return rootContainer.get<RmaListService>(TYPE.RmaListService); }
    @observable private _isLoaded: boolean = false;

    get rmaListFiltersStore() {
        return this._rmaListFiltersStore;
    }

	private get iframeService(): IIframeService {
		return rootContainer.get<IIframeService>(TYPE.IframService);
	}

    protected async fetchList(args: IFetchListArgs) {
        try {
            // One of 3 conditions should be true in order to fetch the list:
            //  - list has not ever been loaded - initial loading
            //  - filters were modified
            //  - some rmas were modified (for example inside RMA page)
            if (this._isLoaded && !this._rmaListFiltersStore.hasChanges() && !rmaPageMainStore.getUpdatedRmas()) {
                return;
            }
            const filters = this._rmaListFiltersStore.getUpdatedModel();
            // Legacy page has no tab UI: never send `status_group` (its default `OPEN` would
            // 400 against terminal `filter_status` chips on rms-api) and never opt into
            // `include_tab_counts` (no consumer here).
            const { status_group: _omitStatusGroup, ...filtersWithoutStatusGroup } = filters;
            const rmaListArgs: IFetchRmaListArgs = {
                ...args,
                ...filtersWithoutStatusGroup,
            } as IFetchRmaListArgs;
            const result = await this._rmaListService.fetchRmaList(MANAGE_RETURNS_BACKEND.LARAVEL_PROXY, rmaListArgs);
            this._rmaListFiltersStore.commitChanges();
            runInAction(() => {
                this._isLoaded = true;
            });

            return {
                ...result,
                data: result.data.map(rma => {
                    const store = new RmaListItemStore(rma);
                    return store;
                })
            };
        }
        catch (err) {
            console.error(err);
        }

    }

    public async load() {
        /** handle top frame history changes */
        this.iframeService.navigateReturns({});

        /** if we are navigating from RMA page to the list and the list was not loaded - load and exit */
        if (!this._isLoaded) {
            await this.loadPage(this._page);
            return;
        }

        /** if list was rendered and then some rmas were updated before user navigated back to the list - reload it  */
        const updatedRmaIds = rmaPageMainStore.getUpdatedRmas();
        if (updatedRmaIds.length > 0) {
            await this.loadPage(this._page);
            rmaPageMainStore.clearUpdatedRmaIds();
        }
    }

    public resetIsLoaded() {
        this._isLoaded = false;
    }
}

export default new RmaPagedListStore();
